import { api, type KeyDirectoryEntry } from '../api/client';
import { getAppState } from '../app/index';
import { createEmptySharedFolder } from '../crypto/sharedFolderCreate';
import {
  inviteSharedFolderMember,
  type SharedFolderMemberPermission,
} from '../crypto/sharedFolderMemberInvite';
import { applyI18n, t } from '../i18n';
import { apiErrorMessage } from '../i18n/apiErrorMessage';
import { bindLocaleRefresh } from '../i18n/bindLocaleRefresh';
import { dialogHelpTopic } from '../help/dialogHelpTopics';
import { bindModalDismiss, createModalShell } from './modalDialogShell';

export interface CreateSharedFolderResult {
  id: string;
  label?: string;
  inviteFailures?: number;
  inviteAttempts?: number;
}

interface PendingInvite {
  userId: string;
  displayName: string;
  permission: SharedFolderMemberPermission;
}

export function showSharedFolderCreateDialog(): Promise<CreateSharedFolderResult | null> {
  return new Promise((resolve) => {
    const shell = createModalShell({
      titleKey: 'files.owner.createTitle',
      titleId: 'files-create-dialog-title',
      helpTopic: dialogHelpTopic('sharedFolderCreate'),
    });

    shell.body.innerHTML = `
      <p class="muted" data-i18n="files.owner.createSubtitle"></p>
      <form class="form-grid" id="files-create-dialog-form">
        <label>
          <span data-i18n="files.owner.label"></span>
          <input type="text" name="label" maxlength="200" autocomplete="off" />
          <span class="muted" data-i18n="files.owner.labelHint"></span>
        </label>
        <label>
          <span data-i18n="files.owner.expiresIn"></span>
          <span class="input-with-unit">
            <input type="number" name="expiresDays" class="input-compact" min="1" max="90" value="7" required />
            <span data-i18n="files.owner.expiresDays"></span>
          </span>
        </label>
        <fieldset class="files-create-invite-fieldset">
          <legend data-i18n="files.owner.createInviteSection"></legend>
          <p class="muted" data-i18n="files.owner.createInviteHint"></p>
          <div class="files-create-invite-row">
            <label>
              <span data-i18n="files.owner.inviteUser"></span>
              <select name="inviteUserId" id="files-create-invite-user">
                <option value="" data-i18n="files.owner.inviteUserPlaceholder"></option>
              </select>
            </label>
            <label>
              <span data-i18n="files.owner.permission"></span>
              <select name="invitePermission">
                <option value="Read" data-i18n="files.owner.accessRead"></option>
                <option value="ReadWrite" data-i18n="files.owner.accessReadWrite"></option>
                <option value="Full" data-i18n="files.owner.accessFull"></option>
              </select>
            </label>
            <button type="button" class="button ghost" id="files-create-invite-add">
              <span data-i18n="files.owner.createInviteAdd"></span>
            </button>
          </div>
          <ul class="files-create-invite-list" id="files-create-invite-list" hidden></ul>
        </fieldset>
        <p class="error-text" id="files-create-dialog-error" hidden></p>
      </form>
    `;

    const cancelButton = document.createElement('button');
    cancelButton.type = 'button';
    cancelButton.className = 'button ghost';
    cancelButton.id = 'files-create-dialog-cancel';
    cancelButton.innerHTML = '<span data-i18n="common.cancel"></span>';

    const submitButton = document.createElement('button');
    submitButton.type = 'submit';
    submitButton.className = 'button primary';
    submitButton.id = 'files-create-dialog-submit';
    submitButton.setAttribute('form', 'files-create-dialog-form');
    submitButton.innerHTML = '<span data-i18n="files.owner.create"></span>';

    shell.actions.append(cancelButton, submitButton);
    document.body.append(shell.backdrop);
    applyI18n(shell.dialog);

    const form = shell.body.querySelector('#files-create-dialog-form') as HTMLFormElement;
    const errorEl = shell.body.querySelector('#files-create-dialog-error') as HTMLParagraphElement;
    const labelInput = form.querySelector('input[name="label"]') as HTMLInputElement;
    const userSelect = form.querySelector('#files-create-invite-user') as HTMLSelectElement;
    const permissionSelect = form.querySelector('select[name="invitePermission"]') as HTMLSelectElement;
    const addInviteButton = form.querySelector('#files-create-invite-add') as HTMLButtonElement;
    const inviteListEl = form.querySelector('#files-create-invite-list') as HTMLUListElement;
    let submitting = false;
    let candidates: KeyDirectoryEntry[] = [];
    const pendingInvites: PendingInvite[] = [];

    const refreshLocale = () => {
      if (!errorEl.hidden && !errorEl.dataset.i18nKey) {
        errorEl.textContent = t('status.error');
      } else if (!errorEl.hidden && errorEl.dataset.i18nKey) {
        errorEl.textContent = t(errorEl.dataset.i18nKey, {
          failed: errorEl.dataset.failed ?? '0',
          total: errorEl.dataset.total ?? '0',
        });
      }
      applyI18n(shell.dialog);
      renderPendingInvites();
    };

    const unbindLocale = bindLocaleRefresh(refreshLocale);

    const close = (result: CreateSharedFolderResult | null) => {
      if (submitting) {
        return;
      }

      unbindLocale();
      unbindDismiss();
      shell.backdrop.remove();
      resolve(result);
    };

    const unbindDismiss = bindModalDismiss({
      backdrop: shell.backdrop,
      closeButton: shell.closeButton,
      onClose: () => close(null),
    });

    const renderPendingInvites = () => {
      inviteListEl.replaceChildren();
      inviteListEl.hidden = pendingInvites.length === 0;
      for (const invite of pendingInvites) {
        const item = document.createElement('li');
        item.className = 'files-create-invite-item';
        const permissionLabel = invite.permission === 'Full'
          ? t('files.owner.accessFull')
          : invite.permission === 'ReadWrite'
            ? t('files.owner.accessReadWrite')
            : t('files.owner.accessRead');
        item.textContent = `${invite.displayName} · ${permissionLabel}`;
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'button ghost';
        remove.textContent = t('common.cancel');
        remove.addEventListener('click', () => {
          const index = pendingInvites.findIndex((entry) => entry.userId === invite.userId);
          if (index >= 0) {
            pendingInvites.splice(index, 1);
          }
          refillUserSelect();
          renderPendingInvites();
        });
        item.append(remove);
        inviteListEl.append(item);
      }
    };

    const refillUserSelect = () => {
      const pendingIds = new Set(pendingInvites.map((entry) => entry.userId));
      const available = candidates.filter((entry) => !pendingIds.has(entry.userId));
      userSelect.replaceChildren();
      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.setAttribute('data-i18n', 'files.owner.inviteUserPlaceholder');
      userSelect.append(placeholder);
      for (const candidate of available) {
        const option = document.createElement('option');
        option.value = candidate.userId;
        option.textContent = candidate.displayName;
        userSelect.append(option);
      }
      applyI18n(userSelect);
      const disabled = available.length === 0;
      userSelect.disabled = disabled;
      addInviteButton.disabled = disabled;
    };

    void (async () => {
      try {
        const state = getAppState();
        const currentUserId = state.kind === 'authenticated' ? state.session.userId : '';
        const directory = await api.getKeyDirectory();
        candidates = directory.filter((entry) => entry.userId !== currentUserId);
        refillUserSelect();
      } catch (cause) {
        console.error('Load invite candidates failed', cause);
        userSelect.disabled = true;
        addInviteButton.disabled = true;
      }
    })();

    const queueInviteFromSelect = (): void => {
      const userId = userSelect.value.trim();
      if (!userId) {
        return;
      }
      const candidate = candidates.find((entry) => entry.userId === userId);
      if (!candidate || pendingInvites.some((entry) => entry.userId === userId)) {
        return;
      }
      pendingInvites.push({
        userId,
        displayName: candidate.displayName,
        permission: permissionSelect.value as SharedFolderMemberPermission,
      });
      refillUserSelect();
      renderPendingInvites();
    };

    addInviteButton.addEventListener('click', () => {
      queueInviteFromSelect();
    });

    cancelButton.addEventListener('click', () => close(null));

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      if (submitting) {
        return;
      }

      void (async () => {
        submitting = true;
        errorEl.hidden = true;
        delete errorEl.dataset.i18nKey;
        submitButton.disabled = true;

        try {
          // Include the currently selected user even if Add was not clicked.
          queueInviteFromSelect();

          const days = Number.parseInt(String(new FormData(form).get('expiresDays') ?? '7'), 10);
          const label = String(new FormData(form).get('label') ?? '').trim();
          const expiresAt = new Date(Date.now() + days * 24 * 60 * 60 * 1000);
          const created = await createEmptySharedFolder(expiresAt, label || undefined);

          let inviteFailures = 0;
          for (const invite of pendingInvites) {
            try {
              await inviteSharedFolderMember({
                folderId: created.id,
                folderKey: created.folderKey,
                userId: invite.userId,
                permission: invite.permission,
              });
            } catch (cause) {
              console.error('Invite on create failed', cause);
              inviteFailures += 1;
            }
          }

          submitting = false;
          unbindLocale();
          unbindDismiss();
          shell.backdrop.remove();
          resolve({
            id: created.id,
            label: label || undefined,
            inviteFailures: inviteFailures > 0 ? inviteFailures : undefined,
            inviteAttempts: pendingInvites.length > 0 ? pendingInvites.length : undefined,
          });
        } catch (cause) {
          submitting = false;
          submitButton.disabled = false;
          errorEl.hidden = false;
          errorEl.textContent = apiErrorMessage(cause);
        }
      })();
    });

    labelInput.focus();
  });
}
